
/**
 * Module dependencies.
 */

/* This is needed to import the DateRangePicker component */
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { DateRangePicker } from 'react-dates';
import { FieldError } from '../../forms/field-error';
import { FormControlWrapper, FormGroup } from '../../forms/fields-styled-components';
import { Icon } from 'react-components/media';
import { ReactNode, useCallback, useState } from 'react';
import { color, media } from 'react-components/styles';
import { ifProp, prop, theme } from 'styled-tools';
import { isEmpty, isNil } from 'lodash';
import { typography } from '../../typography/typography-styles';
import { useField } from '@seegno/react-forms';
import calendarIcon from '../../assets/svg/32/calendar.svg';
import moment from 'moment';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  columnSize: string;
  customArrowIcon?: ReactNode;
  disabled?: boolean;
  endDateName: string;
  endDatePlaceholder: string;
  maxDate?: string;
  minDate?: string;
  name: string;
  startDateName: string;
  startDatePlaceholder: string;
};

/**
 * `StyledFormControlWrapper` styled component.
 */

const StyledFormControlWrapper = styled(FormControlWrapper)`
  align-items: initial;
  column-gap: initial;
  display: block;
`;

/**
 * `DateRangePickerWrapper` styled component.
 */

const DateRangePickerWrapper = styled.div<{ columnSize: string; disabled: boolean; hasStartDateValue: boolean }>`
  height: 50px;
  position: relative;

  ${media.min('md')`
    height: 54px;
  `}

  .DateRangePicker {
    height: 100%;
    width: 100%;

    > div {
      height: 100%;
    }

    &Input {
      align-items: center;
      background-color: transparent;
      column-gap: 8px;
      display: grid;
      grid-template-columns: ${prop('columnSize')} max-content minmax(${prop('columnSize')}, 1fr) max-content;
      height: 100%;
      padding: 0 10px 0 20px;

      ${ifProp('hasStartDateValue', css`
        grid-template-columns: 85px max-content minmax(85px, 1fr) max-content;
      `)}

      &_calendarIcon {
        border-radius: 50%;
        grid-column: -1;
        height: 40px;
        margin: 0;
        padding: 8px;
        transition: background-color ${theme('transitions.defaultTransition')};
        width: 40px;

        &:focus,
        &:hover {
          background-color: ${color('gray300')};
        }
      }

      &_arrow {
        color: ${color('gray500')};
        transition: color ${theme('transitions.defaultTransition')};

        ${ifProp('disabled', css`
          color: ${color('gray400')};
        `)}
      }
    }

    &_picker {
      border-radius: 12px;
      z-index: ${theme('zIndex.dateRangeInput.picker')};

      .CalendarDay {
        &__default {
          transition: ${theme('transitions.defaultTransition')};
          transition-property: background-color, border-color, color;

          &:focus,
          &:hover {
            background-color: ${color('gray300')};
            border-color: ${color('gray300')};
            color: ${color('secondary')};
          }
        }

        &__hovered {
          &_span {
            &,
            &:focus,
            &:hover {
              background-color: ${color('gray300')};
              border-color: ${color('gray300')};
              color: ${color('secondary')};
            }
          }
        }

        &__selected {
          transition: ${theme('transitions.defaultTransition')};
          transition-property: background-color, border-color, color;

          &,
          &:active {
            background-color: ${color('gray400')};
            border-color: ${color('gray400')};
            color: ${color('secondary')};
          }

          &:hover {
            background-color: ${color('gray300')};
            border-color: ${color('gray300')};
          }

          &_span {
            background-color: ${color('gray400')};
            border-color: ${color('gray400')};
            color: ${color('secondary')};
          }
        }
      }

      .CalendarMonth_caption {
        padding-bottom: 42px;
      }
    }
  }

  .DateInput {
    background: transparent;
    width: 100%;

    &_input {
      ${typography.styles.textNormal}

      background-color: transparent;
      border: none;
      color: ${color('secondary')};
      padding: 8px 0;
      transition: color ${theme('transitions.defaultTransition')};

      &__disabled {
        color: ${color('gray400')};
        font-style: initial;
      }

      &::-webkit-input-placeholder {
        color: ${color('gray500')};
        font-size: inherit;
        font-weight: inherit;
        line-height: inherit;

        ${ifProp('disabled', css`
          color: ${color('gray400')};
        `)}
      }
    }

    &_fang {
      top: 46px !important;
      z-index: ${theme('zIndez.dateRangeInput.fang')};
    }
  }

  .DayPicker {
    border-radius: 12px;

    &_transitionContainer {
      border-radius: 12px;
    }
  }
`;

/**
 * `StyledIcon` styled component.
 */

const StyledIcon = styled(Icon)<{ disabled: boolean }>`
  transition: color ${theme('animations.defaultTransition')};

  ${ifProp('disabled', css`
    color: ${color('gray400')};
  `)}
`;

/**
 * Export `DateRangeInput` component.
 */

export function DateRangeInput(props: Props): JSX.Element {
  const {
    customArrowIcon = '-',
    disabled = false,
    endDateName,
    endDatePlaceholder,
    columnSize,
    maxDate = moment().startOf('date').add(1, 'd'),
    minDate,
    name,
    startDateName,
    startDatePlaceholder
  } = props;

  const [focusedInput, setFocusedInput] = useState<any>(null);
  const {
    error: endDateError,
    meta: endDateMeta,
    onBlur: onBlurEndDate,
    onChange: onChangeEndDate,
    onFocus: onFocusEndDate,
    value: endDate
  } = useField(endDateName);

  const {
    error: startDateError,
    meta: startDateMeta,
    onBlur: onBlurStartDate,
    onChange: onChangeStartDate,
    onFocus: onFocusStartDate,
    value: startDate
  } = useField(startDateName);

  const hasError = !!endDateError && endDateMeta?.touched || !!startDateError && startDateMeta?.touched;
  const hasFocus = endDateMeta?.active || startDateMeta?.active;
  const displayFormat = moment.localeData().longDateFormat('L');
  const handleFocusChange = useCallback((values: any) => {
    if (!isEmpty(values)) {
      setFocusedInput(values);
      onFocusEndDate();
      onFocusStartDate();

      return;
    }

    setFocusedInput(null);
    onBlurEndDate();
    onBlurStartDate();
  }, [onBlurEndDate, onBlurStartDate, onFocusEndDate, onFocusStartDate]);

  return (
    <FormGroup disabled={disabled}>
      <StyledFormControlWrapper
        disabled={disabled}
        hasError={hasError}
        hasFocus={hasFocus}
      >
        <DateRangePickerWrapper
          columnSize={columnSize}
          disabled={disabled}
          hasStartDateValue={!isNil(startDate)}
        >
          <DateRangePicker
            customArrowIcon={customArrowIcon}
            customInputIcon={(
              <StyledIcon
                aria-hidden
                color={color('gray500')}
                disabled={disabled}
                icon={calendarIcon}
                size={'24px'}
              />
            )}
            disabled={disabled}
            displayFormat={displayFormat}
            endDate={endDate ? moment(endDate, displayFormat) : null}
            endDateId={endDateName}
            endDatePlaceholderText={endDatePlaceholder}
            focusedInput={focusedInput}
            hideKeyboardShortcutsPanel
            inputIconPosition={'after'}
            isOutsideRange={date => date.isSameOrAfter(maxDate)}
            maxDate={maxDate ? moment(maxDate) : undefined}
            minDate={minDate ? moment(minDate) : undefined}
            noBorder
            numberOfMonths={1}
            onDatesChange={({ endDate, startDate }) => {
              onChangeStartDate(isNil(startDate) ? undefined : moment(startDate).format(displayFormat));
              onChangeEndDate(isNil(endDate) ? undefined : moment(endDate).format(displayFormat));
            }}
            onFocusChange={handleFocusChange}
            startDate={startDate ? moment(startDate, displayFormat) : null}
            startDateId={startDateName}
            startDatePlaceholderText={startDatePlaceholder}
          />
        </DateRangePickerWrapper>
      </StyledFormControlWrapper>

      <FieldError
        error={endDateError || startDateError}
        isOpen={!!endDateError || !!startDateError}
        name={name}
      />
    </FormGroup>
  );
}
